import { useState, useMemo, type ChangeEventHandler, type ReactNode } from "react";
import Select from "react-select";
import { useAppSelector } from "../../../../redux/hooks";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import { token } from "../../../../../styled-system/tokens";
import { Button } from "../../../../components/core/basic/Button";
import { FaTrash } from "react-icons/fa6";
import { css, cx } from "../../../../../styled-system/css";
import { Dialog, Popover } from "react-aria-components";
import { popoverStyles } from "../../../../components/fields/styles/styles";
import { Flex, HStack } from "../../../../../styled-system/jsx";
import { Switch } from "../../../../components/fields/base/Switch";
import { MediaPlanFilterSelect } from "../../../../components/table/MediaPlanFilterSelect";
import { useTranslation } from "react-i18next";

// Operator metadata is keyed by format. Labels are rendered via t() at the call site
// so options stay localized without coupling this module to a specific t scope.
const FORMAT_OPERATOR_VALUES: Record<string, string[]> = {
  toggle: ["all", "on", "off"],
  currency: [">", "<", "≥", "≤", "=", "-"],
  number: [">", "<", "≥", "≤", "=", "-"],
  percent: [">", "<", "≥", "≤", "=", "-"],
  date: ["-", ">=", "<", "="],
  "single-select": ["="],
  select: ["anyof"],
  "multi-select": ["anyof"],
  "media-plan": ["anyof"],
  default: ["⊃", "=", "≠", "^", "$", "∌"],
};

const getOperatorValues = (format: string | undefined) =>
  FORMAT_OPERATOR_VALUES[format ?? "default"] ?? FORMAT_OPERATOR_VALUES.default!;

// Public helper used by FilterWidget; only the .value is consumed externally.
export const getFormatFilterOptions = (format: string | undefined) =>
  getOperatorValues(format).map((value) => ({ value, label: value }));

interface Props {
  format?: string;
  title?: ReactNode;
  selectOptions?: { label: string; value: unknown }[];
  initialValues: { op?: string; value: unknown };
  onPressRemove: () => void;
  onApply: (value: { op: string; value: string | [string, string] | string[] }) => void;
}

const FilterForm = ({ initialValues, onPressRemove, format, title, onApply, selectOptions }: Props) => {
  const { t } = useTranslation(["merchandising", "common"]);
  // Operator label is sometimes context-dependent — "=" reads "equals" for numbers,
  // "on" for dates, and "is" for text — so we resolve labels per format here.
  const filterOperatorOptions = useMemo(() => {
    const labelFor = (op: string): string => {
      switch (op) {
        case "all":
          return t(($) => $.common["All"]);
        case "on":
          return t(($) => $.merchandising["Active"]);
        case "off":
          return t(($) => $.merchandising["Inactive"]);
        case ">":
          return t(($) => $.merchandising["greater than"]);
        case "<":
          return format === "date" ? t(($) => $.merchandising["before"]) : t(($) => $.merchandising["less than"]);
        case "≥":
          return t(($) => $.merchandising["greater than or equal to"]);
        case "≤":
          return t(($) => $.merchandising["less than or equal to"]);
        case "=":
          if (format === "date") return t(($) => $.merchandising["on"]);
          if (format === "text" || !format) return t(($) => $.merchandising["is"]);
          return t(($) => $.merchandising["equals"]);
        case "-":
          return format === "date" ? t(($) => $.merchandising["between"]) : t(($) => $.merchandising["range"]);
        case ">=":
          return t(($) => $.merchandising["since"]);
        case "anyof":
          return t(($) => $.merchandising["includes any of"]);
        case "⊃":
          return t(($) => $.merchandising["contains"]);
        case "≠":
          return t(($) => $.merchandising["is not"]);
        case "^":
          return t(($) => $.merchandising["starts with"]);
        case "$":
          return t(($) => $.merchandising["ends with"]);
        case "∌":
          return t(($) => $.merchandising["does not contain"]);
        default:
          return op;
      }
    };
    return getOperatorValues(format).map((value) => ({ value, label: labelFor(value) }));
  }, [format, t]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [operator, setOperator] = useState(initialValues.op ?? filterOperatorOptions[0]!.value);
  const [value, setValue] = useState(initialValues.value);

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();
    setValue(e.target.value);
  };

  const formatPercentageValue = (val: unknown): string => {
    if (isNaN(parseFloat(val as string))) {
      return "";
    }
    return Math.round(Number(val) * 100).toString();
  };

  return (
    <Popover className={cx(popoverStyles, css({ minWidth: "200px", maxWidth: "33vw" }))}>
      <Dialog>
        {({ close }) => (
          <form
            className={css({
              display: "flex",
              gap: "xsmall",
              flexDirection: "column",
              alignItems: "stretch",
              padding: "medium",
            })}
          >
            <HStack justify={"space-between"} width={"100%"} paddingBottom={"small"}>
              <header
                className={css({
                  color: "text.secondary",
                  fontSize: "xsmall",
                  fontWeight: "bold",
                })}
              >
                {title}
              </header>
              {format === "multi-select" && (
                <Flex
                  className={css({
                    gap: "small",
                    alignItems: "center",
                    color: "text.secondary",
                    fontSize: "xsmall",
                    fontWeight: "bold",
                  })}
                >
                  <span>{t(($) => $.merchandising["Any of"])}</span>
                  <Switch
                    isSelected={operator === "allof"}
                    onChange={(isSelected) => {
                      setOperator(isSelected ? "allof" : "anyof");
                    }}
                  />
                  <span>{t(($) => $.merchandising["All of"])}</span>
                </Flex>
              )}
              <Button
                variant="contentOnly"
                onPress={() => {
                  onPressRemove();
                  close();
                }}
              >
                <FaTrash />
              </Button>
            </HStack>

            {filterOperatorOptions.length > 1 && (
              <Select
                styles={{
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    textAlign: "start",
                  }),
                  singleValue: (baseStyles) => ({
                    ...baseStyles,
                    textAlign: "start",
                    fontWeight: "700",
                    color: token("colors.text.secondary"),
                  }),
                }}
                closeMenuOnSelect
                onChange={(value) => {
                  const op = value!.value;
                  if (op === "-") {
                    setValue(["", ""]);
                  } else {
                    setValue("");
                  }
                  setOperator(value!.value);
                }}
                options={filterOperatorOptions}
                value={filterOperatorOptions.find((option) => option.value === operator)}
              />
            )}
            {(() => {
              switch (format) {
                case "media-plan":
                  return (
                    <MediaPlanFilterSelect
                      value={Array.isArray(value) ? (value as (string | number)[]) : []}
                      onChange={(values) => setValue(values)}
                    />
                  );
                case "single-select":
                  return (
                    <Select
                      isClearable={value !== "all"}
                      styles={{
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          textAlign: "start",
                        }),
                        singleValue: (baseStyles) => ({
                          ...baseStyles,
                          textAlign: "start",
                          fontWeight: "700",
                          color: token("colors.text.secondary"),
                        }),
                      }}
                      closeMenuOnSelect
                      onChange={(selected) => {
                        setValue(selected ? selected.value : "all");
                      }}
                      options={selectOptions}
                      value={selectOptions?.find((o) => o.value === value)}
                    />
                  );
                case "select":
                case "multi-select":
                  return (
                    <Select
                      isMulti
                      isClearable={!(value as string[] | null)?.includes("all")}
                      styles={{
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          textAlign: "start",
                        }),
                        multiValue: (baseStyles) => ({
                          ...baseStyles,
                          backgroundColor: "transparent",
                          textAlign: "start",
                          fontWeight: "700",
                          color: token("colors.text.secondary"),
                        }),
                        multiValueRemove: (baseStyles, props) => ({
                          ...baseStyles,
                          display: props.data.value === "all" ? "none" : "block",
                        }),
                      }}
                      closeMenuOnSelect={false}
                      onChange={(changingValues) => {
                        let nextValues = changingValues.map((v) => v.value);
                        if (!(value as string[]).includes("all") && nextValues.includes("all")) {
                          nextValues = ["all"];
                        } else {
                          nextValues = nextValues.filter((v) => v !== "all");
                        }

                        if (nextValues.length === 0) {
                          nextValues.push("all");
                        }
                        setValue(nextValues);
                      }}
                      options={selectOptions}
                      value={selectOptions?.filter((o) => (value as string).includes(o.value as string))}
                    />
                  );
                case "currency":
                case "number":
                  if (operator === "-") {
                    return (
                      <div className="py-2 d-flex align-items-center">
                        <span
                          className={`${
                            format === "currency" ? "prefix-input-wrapper prefix-" + marketPlace.toLowerCase() : ""
                          }`}
                          style={{ width: "43%" }}
                        >
                          <input
                            type={"number"}
                            className={`fs-6 border  ${format === "currency" ? "prefix-input" : ""}`}
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              width: "100%",
                            }}
                            placeholder=""
                            value={(value as [string, string])[0]}
                            onChange={(e) => {
                              setValue([e.target.value, (value as [string, string])[1]]);
                            }}
                          />
                        </span>
                        <span style={{ width: "14%", textAlign: "center" }}>{t(($) => $.merchandising["to"])}</span>
                        <span
                          className={`${
                            format === "currency" ? "prefix-input-wrapper prefix-" + marketPlace.toLowerCase() : ""
                          }`}
                          style={{
                            width: "43%",
                          }}
                        >
                          <input
                            type={"number"}
                            className={`fs-6 border ${format === "currency" ? "prefix-input" : ""}`}
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              width: "100%",
                            }}
                            placeholder=""
                            value={(value as [string, string])[1]}
                            onChange={(e) => {
                              setValue([(value as [string, string])[0], e.target.value]);
                            }}
                          />
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div className="py-2 d-flex align-items-center">
                      <span
                        className={`${
                          format === "currency" ? "prefix-input-wrapper prefix-" + marketPlace.toLowerCase() : ""
                        } w-100`}
                      >
                        <input
                          type={"number"}
                          className={`fs-6 border ${format === "currency" ? "prefix-input" : ""}`}
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            width: "100%",
                          }}
                          placeholder=""
                          name="value"
                          value={value as string}
                          onChange={onChange}
                        />
                      </span>
                    </div>
                  );
                case "percent":
                  if (operator === "-") {
                    return (
                      <div className="py-2 d-flex align-items-center">
                        <span className={"suffix-input-wrapper suffix-percent"} style={{ width: "43%" }}>
                          <input
                            type={"number"}
                            className={`fs-6 border suffix-input`}
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              width: "100%",
                            }}
                            placeholder=""
                            value={formatPercentageValue((value as [string, string])[0])}
                            onChange={(e) => {
                              setValue([(Number(e.target.value) / 100).toString(), (value as [string, string])[1]]);
                            }}
                          />
                        </span>
                        <span style={{ width: "14%", textAlign: "center" }}>{t(($) => $.merchandising["to"])}</span>
                        <span
                          className={"suffix-input-wrapper suffix-percent"}
                          style={{
                            width: "43%",
                          }}
                        >
                          <input
                            type={"number"}
                            className={`fs-6 border suffix-input`}
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              width: "100%",
                            }}
                            placeholder=""
                            value={formatPercentageValue((value as [string, string])[1])}
                            onChange={(e) => {
                              setValue([(value as [string, string])[0], (Number(e.target.value) / 100).toString()]);
                            }}
                          />
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div className="py-2 d-flex align-items-center">
                      <span className={"suffix-input-wrapper suffix-percent w-100"}>
                        <input
                          type={"number"}
                          className={`fs-6 border suffix-input`}
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            width: "100%",
                          }}
                          placeholder=""
                          name="value"
                          value={value as string}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setValue(raw);
                          }}
                        />
                      </span>
                    </div>
                  );

                case "date":
                  return (
                    <div className="d-flex align-items-center">
                      <div className={operator === "-" ? "pe-3" : " w-100 pt-3"}>
                        {operator === "-" && (
                          <div className="py-3 pb-3">
                            <label className="w-100 text-start">{t(($) => $.common.breadcrumbDates["Start"])}</label>
                          </div>
                        )}
                        <ReactDatePicker
                          selected={
                            operator === "-" ?
                              (value as [string, string])[0] ?
                                new Date((value as [string, string])[0])
                              : null
                            : value ?
                              new Date(value as string)
                            : null
                          }
                          onChange={(date, e) => {
                            e?.preventDefault();
                            const formattedDate =
                              moment(date).toISOString() === "Invalid date" ? null : moment(date).toISOString();
                            if (operator === "-") {
                              setValue([formattedDate, (value as [string, string])[1]]);
                            } else {
                              setValue(formattedDate);
                            }
                          }}
                          autoComplete="off"
                          popperPlacement="bottom"
                        />
                      </div>
                      {operator === "-" && (
                        <div className={"ps-3"}>
                          <div className="py-3 pb-3">
                            <label className="w-100 text-start">{t(($) => $.common.breadcrumbDates["End"])}</label>
                          </div>
                          <ReactDatePicker
                            selected={(value as [string, string])[1] ? new Date((value as [string, string])[1]) : null}
                            onChange={(date, e) => {
                              e?.preventDefault();
                              const formattedDate =
                                moment(date).toISOString() === "Invalid date" ? null : moment(date).toISOString();

                              setValue([(value as [string, string])[0], formattedDate]);
                            }}
                            autoComplete="off"
                            popperPlacement="bottom"
                          />
                        </div>
                      )}
                    </div>
                  );

                default:
                  return (
                    <div className="py-2 d-flex align-items-center">
                      <span
                        className={`${
                          format === "currency" ? "prefix-input-wrapper prefix-" + marketPlace.toLowerCase()
                          : format === "percent" ? "suffix-input-wrapper suffix-percent"
                          : ""
                        } w-100`}
                      >
                        <input
                          type={"text"}
                          className={`fs-6 border ${
                            format === "currency" ? "prefix-input"
                            : format === "percent" ? "suffix-input"
                            : ""
                          }`}
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            width: "100%",
                          }}
                          placeholder=""
                          name="value"
                          defaultValue={value as string}
                          onChange={onChange}
                        />
                      </span>
                    </div>
                  );
              }
            })()}
            <Button
              onPress={() => {
                let finalValue: string | [string, string] = value as string;

                if (format === "percent") {
                  const normalize = (v: string) => {
                    const num = parseFloat(v);
                    if (isNaN(num)) return "";
                    return num.toString();
                  };

                  if (operator === "-") {
                    finalValue = (value as [string, string]).map(normalize) as [string, string];
                  } else {
                    const num = parseFloat(value as string);
                    finalValue = isNaN(num) ? "" : (num / 100).toString();
                  }
                }

                onApply({ op: operator, value: finalValue });
                close();
              }}
            >
              {t(($) => $.common.actions["Apply"])}
            </Button>
          </form>
        )}
      </Dialog>
    </Popover>
  );
};

export default FilterForm;
